import { CommonButton } from 'components/Buttons';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import actions from 'state/actions';
import selectors from 'state/selectors';
import utils from 'utils';
import { modalActionType, actionType } from 'utils/constants';
import useCheckBeforeAction from '../hooks';
import { lucyModuleKey } from 'utils/configs';
import services from 'services';
import { useFormikContext } from 'formik';
import { FormValues, Q1Value } from 'models/documents';
import { useLocation } from 'react-router-dom';

const MissingVendor = () => {
  const dispatch = useDispatch();
  const protocol = useSelector(selectors.documents.selectActiveDocumentID);
  const records = useSelector(selectors.documents.selectAllActiveDocuments);
  const protocols = useSelector(selectors.documents.selectAllActiveDocumentsID);
  const programCode = useSelector(selectors.app.getProgramCode);
  const userCompanies = useSelector(selectors.app.getCompanies);

  const { pathname } = useLocation();
  const { checkDocuments } = useCheckBeforeAction(actionType.MISSING_VENDOR);
  const { setStatus } = useFormikContext<{ [key: string]: Q1Value | FormValues }>() || {};

  const isInEditDocument = pathname.includes('/edit');

  const t = utils.intl.useTranslator();
  const textTranslate = utils.app.textTranslated;
  const disabled =
    !utils.user.isActionEnabled(records, actionType.MISSING_VENDOR) || (!isInEditDocument && protocols.length > 1);
  const saveDocument = utils.hooks.useSaveDocument();

  const getProgramCode = (idProgram: number) => {
    return lucyModuleKey[idProgram as keyof typeof lucyModuleKey] || '';
  };

  const companyId = records[0]?.idCompany;

  const company = userCompanies.find((c) => c.idCompany === companyId);
  const idAction = company?.actions.find((a) => a.type === actionType.MISSING_VENDOR)?.idAction;

  const applyRules = async () => {
    try {
      if (protocol) {
        const lucyModule = getProgramCode(programCode);
        const { data } = await services.checkMandatoryFields({ protocol, rulesGroup: lucyModule }, 'modal');
        if (isInEditDocument) {
          const status = data.fields.map((el: any) => ({ idField: el.idField, status: el.fieldStatus }));
          setStatus(status);
        }

        if (data.docStatus === 1) {
          utils.app.notify(
            'fail',
            textTranslate(data.docMessageStatus, t) || t('error-submitting-the-document'),
            10000,
          );
          if (data.docMessageStatusDetails?.length) {
            dispatch(
              actions.modal.openModal(modalActionType.documents.SHOW_RESPONSE, {
                title: 'Status Details',
                content: data.docMessageStatusDetails,
              }),
            );
          }
          return;
        } else if (data.docStatus === 2) {
          dispatch(
            actions.modal.openModal(modalActionType.documents.VALIDATION_CONFIRM_MODAL, {
              title: 'missing_vendor_confirmation',
              subTitle: 'confirm_apply_rules_missing_vendor',
              onConfirm: () => {
                dispatch(actions.modal.closeModal());
                dispatch(actions.modal.openModal(modalActionType.documents.SEND_TO_WORKFLOW));
              },
            }),
          );
          return;
        }
        dispatch(
          actions.modal.openModal(modalActionType.documents.SEND_TO_WORKFLOW, {
            idAction,
          }),
        );
      }
    } catch (e) {
      utils.app.notify('fail', textTranslate('error-submitting-the-document', t), 10000);
      throw e;
    }
  };

  const onMissingVendorClick = async () => {
    const docFree = await checkDocuments();
    if (!docFree) return;
    const val = await saveDocument();
    if (isInEditDocument && val == null) {
      return;
    }

    applyRules();
  };

  return (
    <CommonButton
      id={actionType.MISSING_VENDOR}
      action={onMissingVendorClick}
      disabled={disabled}
      scope="tertiary"
      value={t('missingVendor')}
      customIcon="warning"
    />
  );
};

export default MissingVendor;
